/**
 * PostHog Event Constants
 * Naming convention: object_action (noun_verb, snake_case)
 */
export const EVENTS = {
  // Auth Events
  USER_SIGNED_UP: 'user_signed_up',
  USER_SIGNED_IN: 'user_signed_in',
  USER_SIGNED_OUT: 'user_signed_out',
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',

  // Page/Tool Navigation
  PAGE_VIEWED: 'page_viewed',
  TOOL_VIEWED: 'tool_viewed',
  TOOL_LAUNCHED: 'tool_launched',

  // CVR Tool
  CVR_CALCULATION_STARTED: 'cvr_calculation_started',
  CVR_CALCULATION_SAVED: 'cvr_calculation_saved',
  CVR_CALCULATION_LOADED: 'cvr_calculation_loaded',
  CVR_CALCULATION_DELETED: 'cvr_calculation_deleted',
  CVR_CALCULATION_RENAMED: 'cvr_calculation_renamed',
  CVR_REPORT_EXPORTED: 'cvr_report_exported',

  // EVM Tool
  EVM_CALCULATION_STARTED: 'evm_calculation_started',
  EVM_CALCULATION_SAVED: 'evm_calculation_saved',
  EVM_CALCULATION_LOADED: 'evm_calculation_loaded',
  EVM_CALCULATION_DELETED: 'evm_calculation_deleted',
  EVM_CALCULATION_RENAMED: 'evm_calculation_renamed',
  EVM_REPORT_EXPORTED: 'evm_report_exported',

  // Target Cost Tool
  TARGET_COST_STARTED: 'target_cost_started',
  TARGET_COST_SAVED: 'target_cost_saved',
  TARGET_COST_LOADED: 'target_cost_loaded',
  TARGET_COST_DELETED: 'target_cost_deleted',
  TARGET_COST_RENAMED: 'target_cost_renamed',
  TARGET_COST_EXPORTED: 'target_cost_exported',

  // Early Warning Register
  EARLY_WARNING_CREATED: 'early_warning_created',
  EARLY_WARNING_UPDATED: 'early_warning_updated',
  EARLY_WARNING_DELETED: 'early_warning_deleted',
  EARLY_WARNING_RENAMED: 'early_warning_renamed',
  EARLY_WARNING_EXPORTED: 'early_warning_exported',

  // WhatsApp Analyzer
  WHATSAPP_CHAT_UPLOADED: 'whatsapp_chat_uploaded',
  WHATSAPP_ANALYSIS_COMPLETED: 'whatsapp_analysis_completed',
  WHATSAPP_REPORT_EXPORTED: 'whatsapp_report_exported',

  // Settings
  SETTINGS_UPDATED: 'settings_updated',

  // Feedback
  FEEDBACK_OPENED: 'feedback_opened',

  // Errors
  ERROR_OCCURRED: 'error_occurred',
  ERROR_BOUNDARY_TRIGGERED: 'error_boundary_triggered',
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

/**
 * Tool identifiers for consistent tracking
 */
export const TOOL_IDS = {
  CVR: 'cvr',
  EVM: 'evm',
  TARGET_COST: 'target-cost',
  EARLY_WARNING: 'early-warning',
  WHATSAPP: 'whatsapp',
} as const;

export type ToolId = (typeof TOOL_IDS)[keyof typeof TOOL_IDS];

/**
 * Tool display names for human-readable events
 */
export const TOOL_NAMES: Record<ToolId, string> = {
  [TOOL_IDS.CVR]: 'CVR Tool',
  [TOOL_IDS.EVM]: 'EVM Tool',
  [TOOL_IDS.TARGET_COST]: 'Target Cost Tool',
  [TOOL_IDS.EARLY_WARNING]: 'Early Warning Register',
  [TOOL_IDS.WHATSAPP]: 'WhatsApp Analyzer',
};

/**
 * Standard event property interfaces
 */
export interface BaseEventProperties {
  timestamp?: string;
  app_version?: string;
}

export interface ToolEventProperties extends BaseEventProperties {
  tool_id: ToolId;
  tool_name: string;
  project_name?: string;
  calculation_id?: string;
}

export interface ExportEventProperties extends ToolEventProperties {
  export_format: 'pdf' | 'csv' | 'docx';
}

export interface ErrorEventProperties extends BaseEventProperties {
  error_message: string;
  error_stack?: string;
  context?: string;
  component_name?: string;
}
